import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { API_BASE_URL } from '../config';

export default function EmployeeSuggest({ value, onChange, placeholder }) {
  const [q, setQ] = useState(value || '');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef();
  const mountedRef = useRef(false);
  const inputRef = useRef(null);
  const portalRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const newQ = value || '';
    setQ(prev => prev === newQ ? prev : newQ);
  }, [value]);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (!q) {
      setItems([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    clearTimeout(timeoutRef.current);
    setLoading(true);
    timeoutRef.current = setTimeout(() => {
      const param = `employeeName=${encodeURIComponent(q)}`;
      fetch(`${API_BASE_URL}/api/employeeSuggestion?${param}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.ok ? r.json() : [])
        .then(data => {
          setItems(data || []);
          setOpen((data || []).length > 0);
          setSelectedIndex(-1);
        })
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timeoutRef.current);
  }, [q]);

  useEffect(() => {
    portalRef.current = document.createElement('div');
    portalRef.current.className = 'employee-suggest-portal';
    document.body.appendChild(portalRef.current);
    return () => {
      if (portalRef.current) {
        document.body.removeChild(portalRef.current);
        portalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    function updatePos() {
      const el = inputRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setDropdownStyle({
        left: rect.left + window.scrollX + 'px',
        top: rect.bottom + window.scrollY + 'px',
        width: rect.width + 'px'
      });
    }
    if (open) {
      updatePos();
      window.addEventListener('resize', updatePos);
      window.addEventListener('scroll', updatePos, true);
    }
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open]);

  useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      const portalEl = portalRef.current;
      const wrapperEl = inputRef.current && inputRef.current.closest('.employee-suggest');
      if (portalEl && wrapperEl) {
        if (portalEl.contains(e.target) || wrapperEl.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div className="employee-suggest" style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        placeholder={placeholder || 'Search employees'}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (items.length) setSelectedIndex(i => (i < items.length - 1 ? i + 1 : 0));
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (items.length) setSelectedIndex(i => (i > 0 ? i - 1 : Math.max(0, items.length - 1)));
          }
          if (e.key === 'Enter') {
            if (selectedIndex >= 0 && items[selectedIndex]) {
              e.preventDefault();
              const it = items[selectedIndex];
              setQ(it.EmployeeName || '');
              onChange(it);
              setOpen(false);
              setSelectedIndex(-1);
            } else if (items.length) {
              e.preventDefault();
              const it = items[0];
              setQ(it.EmployeeName || '');
              onChange(it);
              setOpen(false);
              setSelectedIndex(-1);
            }
          }
          if (e.key === 'Escape') {
            setOpen(false);
            setSelectedIndex(-1);
          }
        }}
        style={{ width: 220, padding: '6px 8px' }}
      />
      <input style={{ display: 'none' }} aria-hidden />
      {loading && <div style={{ position: 'absolute', right: 6, top: 8, fontSize: 12, color: '#666' }}>Loading…</div>}
      {(open && items && items.length > 0) && portalRef.current && ReactDOM.createPortal(
        <div className="suggestion-list" style={{ position: 'absolute', ...dropdownStyle, zIndex: 99999, maxHeight: 160, overflow: 'auto' }}>
          {items.map((it, i) => (
            <div
              key={it.Empcode}
              className={"suggestion-item" + (i === selectedIndex ? ' selected' : '')}
              onClick={() => {
                setQ(it.EmployeeName || '');
                onChange(it);
                setOpen(false);
                setSelectedIndex(-1);
              }}
              style={{ padding: 8, cursor: 'pointer', borderBottom: '1px solid #eee' }}
            >
              <div style={{ fontWeight: 600 }}>{it.EmployeeName}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{it.Empcode}</div>
            </div>
          ))}
        </div>,
        portalRef.current
      )}
    </div>
  );
}
